import { Link } from "react-router-dom";
import { ArrowRight, Ear, CheckCircle2, ShieldCheck, Users, Sparkles, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const HERO_IMG = "https://images.unsplash.com/photo-1537735319956-df7db4b6a4e9?crop=entropy&cs=srgb&fm=jpg&q=85";
const CLINIC_IMG = "https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="hero-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Badge className="bg-sky-blue text-primary-blue border border-primary-blue/20 mb-6 px-4 py-1.5 text-sm">
              <Sparkles size={14} className="mr-1.5 text-gold" /> Service 100 % gratuit
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary-blue leading-[1.1]">
              Votre bilan auditif <span className="gold-underline">près de chez vous</span>, sans frais.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed max-w-xl">
              Audition Expert vous met en relation avec un centre auditif de confiance parmi nos partenaires officiels.
              Conseil, devis et prise de rendez-vous <strong className="text-primary-blue">gratuits</strong>.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/prendre-rendez-vous" data-testid="home-cta-appointment">
                <Button className="btn-lg-senior bg-primary-blue hover:bg-[#0a1d36] text-white shadow-lg">
                  Prendre rendez-vous
                  <span className="ml-3 text-gold font-bold">GRATUIT</span>
                  <ArrowRight size={20} className="ml-3" />
                </Button>
              </Link>
              <Link to="/comment-ca-marche" data-testid="home-cta-how">
                <Button variant="outline" className="btn-lg-senior border-2 border-primary-blue text-primary-blue hover:bg-sky-blue">
                  Comment ça marche ?
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-700">
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary-blue" /> RGPD conforme</div>
              <div className="flex items-center gap-2"><Users size={18} className="text-primary-blue" /> Réseaux nationaux</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-primary-blue" /> Sans engagement</div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-gold/20 blur-2xl" />
            <img src={HERO_IMG} alt="Couple senior heureux" className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/5] max-h-[560px]" />
            <div className="absolute -bottom-6 -right-4 bg-white rounded-2xl shadow-2xl p-5 max-w-[220px]" data-testid="home-badge-free">
              <div className="text-gold font-heading text-3xl font-bold">0€</div>
              <div className="text-sm text-slate-600 mt-1">Bilan auditif chez nos partenaires</div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS STRIP */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="text-center text-sm uppercase tracking-widest text-slate-500 mb-6">Nos partenaires officiels</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {["Audio Pour Tous", "VivaSon", "Solution Audition"].map((name) => (
              <div key={name} className="flex items-center justify-center gap-3 py-6 rounded-xl bg-sky-blue/40 border border-slate-200" data-testid={`home-partner-${name.replace(/\s/g, "-").toLowerCase()}`}>
                <Ear size={22} className="text-primary-blue" />
                <span className="font-heading text-xl text-primary-blue font-semibold">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-gold text-sm uppercase tracking-widest font-semibold">Notre approche</div>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-primary-blue mt-3 leading-tight">
            Trois étapes simples pour prendre rendez-vous
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Choisissez votre centre", d: "Recherchez par ville ou code postal parmi nos réseaux partenaires." },
            { n: "02", t: "Décrivez votre demande", d: "Bilan, appareillage, contrôle : indiquez votre motif en quelques clics." },
            { n: "03", t: "Nous confirmons pour vous", d: "Le centre étudie votre demande et notre équipe valide le rendez-vous." },
          ].map((s) => (
            <div key={s.n} className="p-8 rounded-2xl bg-white card-lux border border-slate-100">
              <div className="text-gold font-heading text-4xl font-bold">{s.n}</div>
              <div className="font-heading text-2xl text-primary-blue mt-4">{s.t}</div>
              <p className="mt-3 text-slate-700 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST + IMAGE */}
      <section className="bg-sky-blue/50 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <img src={CLINIC_IMG} alt="Centre auditif moderne" className="rounded-2xl shadow-xl w-full object-cover aspect-[16/11]" />
          <div>
            <div className="text-gold text-sm uppercase tracking-widest font-semibold">Un service de confiance</div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-primary-blue mt-3 leading-tight">
              Des centres audiologiques certifiés, sélectionnés pour vous
            </h2>
            <ul className="mt-8 space-y-4 text-lg text-slate-700">
              {[
                "Audioprothésistes diplômés d'État",
                "Bilan et devis toujours gratuits",
                "Aucun paiement via Audition Expert",
                "Vos données ne sont transmises qu'au centre choisi",
              ].map((l) => (
                <li key={l} className="flex items-start gap-3">
                  <CheckCircle2 className="text-primary-blue shrink-0 mt-1" size={22} />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link to="/prendre-rendez-vous">
                <Button className="btn-lg-senior bg-primary-blue text-white hover:bg-[#0a1d36]">
                  Réserver mon rendez-vous gratuit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-3xl bg-primary-blue text-white p-12 md:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/20 blur-3xl" />
          <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="font-heading text-3xl md:text-4xl">Prêt à mieux entendre ?</h3>
              <p className="mt-4 text-white/85 text-lg">Un conseiller étudie votre demande sous 24 h ouvrées.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
              <Link to="/prendre-rendez-vous">
                <Button className="btn-lg-senior bg-gold text-primary-blue hover:bg-[#c39d33] font-semibold">
                  Demander un rendez-vous
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="btn-lg-senior border-2 border-white text-white hover:bg-white hover:text-primary-blue">
                  <Phone size={18} className="mr-2" /> Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
